import { useState, useCallback } from "react";
import { api } from "@/lib/api-client";
import type { ReportConfig } from "./useSavedReports";

export interface GroupedResult {
  [key: string]: unknown;
  teamName?: string;
  _count: { id: number };
  _avg: { totalDurationMs: number | null; fieldCount: number | null; correctionCount: number | null };
  _sum: { fieldCount: number | null; correctionCount: number | null; overrideCount: number | null };
}

interface RawResult {
  id: string;
  classification: string | null;
  jobTypeId: string;
  primaryOperator: string | null;
  completedAt: string;
  totalDurationMs: number;
  fieldCount: number;
  correctionCount: number;
  overrideCount: number;
  exportStatus: string | null;
  exportDestination: string | null;
  slaMet: boolean | null;
}

export interface QueryResult {
  grouped: GroupedResult[] | null;
  raw: RawResult[] | null;
  groupBy: string | null;
  total: number;
}

export function useQueryReport() {
  const [result, setResult] = useState<QueryResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runQuery = useCallback(async (config: ReportConfig) => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (config.groupBy) params.set("groupBy", config.groupBy);
      if (config.jobTypeId) params.set("jobTypeId", config.jobTypeId);
      if (config.from) params.set("from", config.from);
      if (config.to) params.set("to", config.to);
      if (config.operator) params.set("operator", config.operator);
      if (config.teamId) params.set("teamId", config.teamId);
      if (config.classification) params.set("classification", config.classification);
      if (config.sortBy) params.set("sortBy", config.sortBy);
      if (config.sortDir) params.set("sortDir", config.sortDir);
      params.set("limit", "500");

      const query = params.toString() ? `?${params.toString()}` : "";
      const data = await api.get<Record<string, unknown>>(`/analytics/query${query}`);

      if (data.groupBy) {
        setResult({
          grouped: data.results as GroupedResult[],
          raw: null,
          groupBy: data.groupBy as string,
          total: (data.results as unknown[]).length,
        });
      } else {
        setResult({
          grouped: null,
          raw: data.results as RawResult[],
          groupBy: null,
          total: (data.total as number) ?? 0,
        });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Query failed");
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { result, isLoading, error, runQuery };
}
